/**
 * CatalogPicker — v2.5.907
 *
 * Searchable, filterable machine-model catalog picker for the Add Machine
 * dialog. Backs the enterprise "Add from Catalog" one-click provisioning
 * flow. Once a model is picked the parent form:
 *   • Locks vendor / model / machine_type / telemetry_provider fields
 *   • Sets a hidden `catalog_id` on the form
 *   • On save, routes to POST /api/vending/enterprise/machines/from-catalog
 *     instead of the plain /machines endpoint, which auto-planograms every
 *     slot from the model's `default_layout`.
 */
import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Loader2, Package, X, ChevronDown, Zap, Snowflake, Coffee, Cookie, PackageSearch } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/vending/enterprise`;

const typeIcon = {
  snack: Cookie, cold_drink: Snowflake, combo: Package,
  hot_beverage: Coffee, fresh_food: Snowflake, ppe: Package,
  pharmacy: Package, change_maker: Package,
};

const typeLabel = {
  snack: 'Snack', cold_drink: 'Cold Drink', combo: 'Combo',
  hot_beverage: 'Hot Beverage', fresh_food: 'Fresh Food',
  ppe: 'PPE', pharmacy: 'Pharmacy', change_maker: 'Change Maker',
};

export function CatalogPicker({ onPick, selectedCatalogId }) {
  const [models, setModels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [vendorFilter, setVendorFilter] = useState('');
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/catalog/models`)
      .then(res => { if (alive) { setModels(res.data || []); setLoading(false); } })
      .catch(() => { if (alive) { setLoading(false); } });
    return () => { alive = false; };
  }, []);

  const vendors = useMemo(() => Array.from(new Set(models.map(m => m.vendor))).sort(), [models]);
  const types = useMemo(() => Array.from(new Set(models.map(m => m.machine_type))).sort(), [models]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return models.filter(m => {
      if (typeFilter && m.machine_type !== typeFilter) return false;
      if (vendorFilter && m.vendor !== vendorFilter) return false;
      if (!q) return true;
      const hay = `${m.vendor} ${m.model} ${(m.tags || []).join(' ')} ${m.notes || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [models, query, typeFilter, vendorFilter]);

  const selected = selectedCatalogId ? models.find(m => m.id === selectedCatalogId) : null;

  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/30 p-3 space-y-2" data-testid="catalog-picker">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-sky-700">
          <Zap className="h-3.5 w-3.5" />
          Quick add from catalog
          <span className="text-[10px] font-normal text-sky-600 ml-1">
            {models.length} pre-configured models
          </span>
        </div>
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="text-xs text-sky-600 hover:text-sky-800 font-medium flex items-center gap-0.5"
          data-testid="catalog-toggle-btn"
        >
          {expanded ? 'Hide' : (selected ? 'Change' : 'Browse')}
          <ChevronDown className={cn('h-3 w-3 transition-transform', expanded && 'rotate-180')} />
        </button>
      </div>

      {selected && !expanded && (
        <div className="flex items-center gap-2 bg-white rounded-md p-2 border border-sky-100">
          <div className="w-7 h-7 rounded bg-sky-100 flex items-center justify-center flex-shrink-0">
            {(() => { const I = typeIcon[selected.machine_type] || Package; return <I className="h-3.5 w-3.5 text-sky-600" />; })()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-slate-800 truncate">{selected.vendor} {selected.model}</div>
            <div className="text-[10px] text-slate-500">{typeLabel[selected.machine_type] || selected.machine_type} · {selected.slot_count} slots · {(selected.telemetry_support || []).join(', ')}</div>
          </div>
          <button
            type="button"
            onClick={() => onPick(null)}
            className="text-slate-400 hover:text-red-500"
            title="Clear catalog selection"
            data-testid="catalog-clear-btn"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}

      {expanded && (
        <>
          <div className="relative">
            <PackageSearch className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search Jetinno, Necta, FAS, Bianchi, TCN…"
              className="pl-7 h-8 text-xs bg-white"
              data-testid="catalog-search-input"
            />
          </div>

          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setTypeFilter('')}
              className={cn('text-[10px] px-2 py-0.5 rounded-full border', !typeFilter ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-400')}
            >
              All types
            </button>
            {types.map(t => (
              <button
                key={t}
                type="button"
                onClick={() => setTypeFilter(t)}
                className={cn('text-[10px] px-2 py-0.5 rounded-full border', typeFilter === t ? 'bg-sky-600 text-white border-sky-600' : 'bg-white text-slate-600 border-slate-200 hover:border-sky-400')}
              >
                {typeLabel[t] || t}
              </button>
            ))}
          </div>

          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={() => setVendorFilter('')}
              className={cn('text-[10px] px-2 py-0.5 rounded-full border', !vendorFilter ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400')}
            >
              All vendors
            </button>
            {vendors.map(v => (
              <button
                key={v}
                type="button"
                onClick={() => setVendorFilter(v)}
                className={cn('text-[10px] px-2 py-0.5 rounded-full border', vendorFilter === v ? 'bg-slate-700 text-white border-slate-700' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400')}
              >
                {v}
              </button>
            ))}
          </div>

          <div className="max-h-64 overflow-y-auto space-y-1 pr-1" data-testid="catalog-list">
            {loading && (
              <div className="text-center py-6 text-slate-400 text-xs flex items-center justify-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading catalog…
              </div>
            )}
            {!loading && filtered.length === 0 && (
              <div className="text-center py-6 text-slate-400 text-xs">
                No models match your search.
              </div>
            )}
            {!loading && filtered.map(m => {
              const Icon = typeIcon[m.machine_type] || Package;
              const isSelected = selectedCatalogId === m.id;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { onPick(m); setExpanded(false); }}
                  className={cn(
                    'w-full text-left rounded-md border p-2 flex items-start gap-2 transition-colors',
                    isSelected ? 'border-sky-500 bg-sky-100/60' : 'border-slate-200 bg-white hover:border-sky-400 hover:bg-sky-50',
                  )}
                  data-testid={`catalog-item-${m.id}`}
                >
                  <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                    <Icon className="h-4 w-4 text-slate-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="text-xs font-semibold text-slate-800">{m.vendor}</span>
                      <span className="text-xs text-slate-600">{m.model}</span>
                      <Badge variant="outline" className="text-[9px] py-0 px-1 h-4">{typeLabel[m.machine_type] || m.machine_type}</Badge>
                      {(m.tags || []).slice(0, 2).map(t => (
                        <Badge key={t} variant="secondary" className="text-[9px] py-0 px-1 h-4 bg-slate-100">{t}</Badge>
                      ))}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5 line-clamp-1">{m.notes}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">
                      {m.slot_count} slots · Telemetry: {(m.telemetry_support || []).slice(0, 3).join(', ')}{m.recipe_capable ? ' · recipe-capable' : ''}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

export default CatalogPicker;
